import { ApiError } from '@thyme/libs/src/api/apiRequest'
import { HTTPStatusCode } from '@thyme/libs/src/api/types/statusCodes'
import { peopleApi } from '@/api/entities/people/api'
import { Gender, Person } from '@/api/entities/people/types'
import { useAddressesApi } from '@/legacy/store/modules/addresses'
import { useConfigStore } from '@/legacy/store/modules/config'
import { useEntitiesApi } from '@/legacy/store/modules/entity'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { useContactsApi, usePatientStore } from '@/legacy/store/modules/patient'
import { useMapEntityPhoneNumberApi } from '@/legacy/store/modules/phoneNumbers'
import { IdMap } from '@/legacy/types/api/store'
import {
  CommunicationConsentCategory,
  CommunicationConsentChannel,
  CommunicationConsentSource,
  MapEntityPhoneNumber,
  UpsertCommunicationConsentRequest,
} from '@/legacy/types/entities/phoneNumbers'
import { NULL_STRING } from '@/legacy/types/global/strings'
import { NotificationType } from '@/legacy/types/notifications'
import {
  Contact,
  ContactsFormData,
  NewRelationshipToPatient,
} from '@/legacy/types/patients/contacts'
import { NormalizedPatient } from '@/legacy/types/patients/patients'
import { jsDateToPlainDateString } from '@/libs/date/date'
import { lookupEnum } from '@/libs/lookup/enumLookup'
import { onlyDigits } from '@/libs/phoneNumber'
import {
  getPhoneNumberEntityMaps,
  migrateUnknownTextsToPatient,
} from './sharedPhoneNumberVerificationParts'

/**
 * Creates or updates a Person and its associated Entity. If an existing entity
 * ID is provided, patches the person fields and entity email; otherwise creates
 * a new entity with an embedded person record. Returns null/undefined and logs
 * an error if the API call succeeds but returns no person.
 * @param data - Contact form data containing name, language, DOB, and email
 * @param existingEntityId - Entity ID to update, or undefined when creating
 */
async function _upsertPersonAndEntity(
  data: ContactsFormData,
  existingEntityId: string | undefined
): Promise<Person | null | undefined> {
  const personBody = {
    firstName: data.firstName,
    lastName: data.lastName,
    preferredLanguage: data.preferredLanguage,
    gender: Gender.Unknown,
    ...(data.dateOfBirth
      ? { dateOfBirth: jsDateToPlainDateString(data.dateOfBirth) }
      : {}),
  }

  let person: Person | null | undefined
  if (existingEntityId) {
    person = await peopleApi.partialUpdate({
      ids: [existingEntityId],
      body: personBody,
    })
    if (!person) {
      console.error('Person partial update succeeded but returned no person.')
      return person
    }
    await useEntitiesApi().partialUpdate({
      ids: [existingEntityId],
      body: { email: data.email },
    })
  } else {
    const entity = await useEntitiesApi().create({
      body: { person: personBody, email: data.email },
    })
    person = entity.person
  }

  return person
}

/**
 * Builds address operations from form data and the contact's existing address.
 * Deletes the existing address if no street line is provided, updates it if one
 * exists, or creates a new one. Returns 0 or 1 promises to spread into the
 * shared toAwait batch.
 * @param data - Contact form data containing address fields
 * @param existingAddress - The contact's current address record, or null if none
 * @param personEntityId - Entity ID used when creating a new address record
 */
function _constructAddressDataAndUpsertOrDelete(
  data: ContactsFormData,
  existingAddress: { addressId: string } | null,
  personEntityId: string
): Promise<unknown>[] {
  const ops: Promise<unknown>[] = []
  const addressData = {
    streetAddressLine1: data.streetAddressLine1,
    streetAddressLine2: data.streetAddressLine2,
    city: data.city,
    state: data.state,
    zip: data.zip,
    type: 'home',
  }

  if (existingAddress) {
    const addressId = existingAddress.addressId
    ops.push(
      useAddressesApi().partialUpdate({
        ids: [addressId],
        body: addressData,
      })
    )
  } else if (addressData.streetAddressLine1) {
    ops.push(
      useAddressesApi().create({
        body: { ...addressData, entityId: personEntityId },
      })
    )
  }

  return ops
}

/**
 * Creates or updates the phone number mapping for a contact and checks whether the number
 * was previously mapped to the unknown-texts patient. If so, queues removal of
 * that stale mapping in toAwaitOps so it runs in the same Promise.all batch.
 * Only called when the phone number field has a value.
 * @param data - Contact form data containing the phone number and consent status
 * @param existingPhoneNumber - The contact's current phone number mapping, or null/undefined if none
 * @param personEntityId - Entity ID the phone number should be mapped to
 */
async function _upsertPhoneNumber(
  data: ContactsFormData,
  existingPhoneNumber: MapEntityPhoneNumber | null | undefined,
  personEntityId: string
): Promise<{
  toAwaitOps: Promise<unknown>[]
  wasUnknownNumberMap: MapEntityPhoneNumber | undefined
  phoneUpsertResult: MapEntityPhoneNumber
}> {
  let wasUnknownNumberMap: MapEntityPhoneNumber | undefined
  const TEXTING_UNKNOWN_NUMBERS_PATIENT_ID =
    useConfigStore().configVals?.textingSubtasksUnknownNumberPatientId
  if (TEXTING_UNKNOWN_NUMBERS_PATIENT_ID) {
    const phoneMaps = await getPhoneNumberEntityMaps(data.phoneNumber)
    wasUnknownNumberMap = phoneMaps.find(
      (m) => m.entityId === TEXTING_UNKNOWN_NUMBERS_PATIENT_ID
    )
  }
  // Upsert separately (not in toAwait) to capture result for migration
  const phoneUpsertResult = await useMapEntityPhoneNumberApi().upsert({
    body: {
      entityId: personEntityId,
      phoneNumberId: existingPhoneNumber?.phoneNumberId,
      isPrimary: true,
      phoneNumber: {
        phoneNumber: onlyDigits(data.phoneNumber),
        countryCode: '1',
        messagingOptInStatus: data.messagingOptInStatus,
      },
      communicationConsents: [
        {
          mapEntityPhoneNumberId:
            existingPhoneNumber?.mapEntityPhoneNumberId ?? null,
          channel: CommunicationConsentChannel.SMS,
          consentSource: CommunicationConsentSource.MANUAL,
          category: CommunicationConsentCategory.TRANSACTIONAL,
          isConsented: data.messagingOptInStatus ?? null,
        },
      ] as UpsertCommunicationConsentRequest[],
    },
  })
  // If the number was previously owned by the unknown-texts patient, queue
  // removal of that stale mapping so it runs in the same Promise.all batch.
  const toAwaitOps: Promise<unknown>[] = wasUnknownNumberMap
    ? [
        useMapEntityPhoneNumberApi().delete({
          ids: [wasUnknownNumberMap.mapEntityPhoneNumberId],
        }),
      ]
    : []
  return { toAwaitOps, wasUnknownNumberMap, phoneUpsertResult }
}

/**
 *
 * @param err
 */
function _notifyContactError(err: unknown): void {
  const msg =
    err instanceof ApiError &&
    err.status === HTTPStatusCode.ERROR_409_CONFLICT.valueOf()
      ? err.message
      : 'Failed to create/update contact.'
  useNotificationStore().setNotification({
    message: msg,
    type: NotificationType.DANGER,
  })
}

/**
 *
 */
function _notifyContactSuccess(): void {
  useNotificationStore().setNotification({
    message: 'Successfully created/updated contact.',
    type: NotificationType.SUCCESS,
  })
}

/**
 * Function to help display patient relationship from enum
 * @param relationshipToPatient
 */
export function displayRelationship(
  relationshipToPatient: string | null | undefined
) {
  if (!relationshipToPatient) {
    return 'Unknown Relationship'
  }
  return lookupEnum(
    NewRelationshipToPatient,
    relationshipToPatient.toLowerCase()
  )
}

/**
 * Function to help grab person object from contact nested objs
 * @param contact
 */
export function contactPersonObj(
  contact: Contact | null | undefined
): Person | undefined {
  return contact?.contactEntity?.person
}

/**
 *
 * Function to return matched contact from patient store
 * @param contactEntityId
 * @param contactsMap
 */
export function getContactFromStore(
  contactEntityId: string | null,
  contactsMap: IdMap<Contact>
) {
  const contactsList = Object.values(contactsMap ?? {})
  if (contactsList.length && contactEntityId) {
    return contactsList.find(
      (contact: Contact) => contact.contactEntityId === contactEntityId
    )
  }
  return undefined
}

/**
 *
 * @param data
 * @param patient
 * @param contacts
 * @param editingId
 */
export async function upsertContactData(
  data: ContactsFormData,
  patient: NormalizedPatient | null,
  contacts: IdMap<Contact> | null,
  editingId: string | null | undefined
) {
  let existingEntity, existingEntityId
  if (!patient) return
  if (contacts && editingId) {
    existingEntity = contacts[editingId]
    existingEntityId = existingEntity?.contactEntityId
  }

  try {
    const person = await _upsertPersonAndEntity(data, existingEntityId)
    if (!person) {
      useNotificationStore().setNotification({
        message: 'Person upsert failed.',
        type: NotificationType.DANGER,
        error: new Error('Person upsert failed.'),
      })
      return
    }

    const contactBody = {
      approvedToSpeak: data.approvedToSpeak === 'true',
      relationshipToPatient: data.relationshipToPatient,
      approvedToAccessMedInfo: data.approvedToAccessMedInfo,
      contactEntityId: person.entityId,
      ...(data.isPrimaryCaregiver != null
        ? { isPrimaryCaregiver: data.isPrimaryCaregiver === 'true' }
        : {}),
      ...(data.caregiverPoa != null
        ? { caregiverPoa: data.caregiverPoa === 'true' }
        : {}),
      ...(data.isEmergencyContact != null
        ? { isEmergencyContact: data.isEmergencyContact === 'true' }
        : {}),
    }

    // toAwait is an array of async functions to execute in a Promise.all()
    // as updating contact information requires updating different sets of
    // endpoints
    const toAwait: Promise<unknown>[] = [
      editingId
        ? useContactsApi().partialUpdate({
            body: contactBody,
            ids: [editingId],
          })
        : useContactsApi().create({
            body: {
              ...contactBody,
              patientId: patient.entityId,
              contactEntityId: person.entityId,
            },
          }),
    ]

    const existingAddress = existingEntity?.contactEntity.addresses?.length
      ? existingEntity.contactEntity.addresses[0]
      : null
    toAwait.push(
      ..._constructAddressDataAndUpsertOrDelete(
        data,
        existingAddress,
        person.entityId
      )
    )

    const existingPhoneNumber = existingEntity?.contactEntity.phoneNumbers
      ?.length
      ? existingEntity.contactEntity.phoneNumbers[0]
      : null
    // Check if there were any phone number changes.
    const oldStatus =
      existingPhoneNumber?.phoneNumber.messagingOptInStatus?.toString() ??
      NULL_STRING
    const newStatus =
      data.messagingOptInStatus === null
        ? NULL_STRING
        : data.messagingOptInStatus.toString()
    const isPhoneChanged =
      oldStatus !== newStatus ||
      //compare string versions because it returns false when old val = null and new val = false
      //null indicates waiting for response/unknown
      (existingPhoneNumber?.phoneNumber.phoneNumber ?? '') !== data.phoneNumber

    let wasUnknownNumberMap: MapEntityPhoneNumber | undefined
    let phoneUpsertResult: MapEntityPhoneNumber | undefined

    if (isPhoneChanged) {
      if (!data.phoneNumber && existingPhoneNumber) {
        toAwait.push(
          useMapEntityPhoneNumberApi().delete({
            ids: [existingPhoneNumber.mapEntityPhoneNumberId],
          })
        )
      }

      if (data.phoneNumber) {
        const phoneResult = await _upsertPhoneNumber(
          data,
          existingPhoneNumber,
          person.entityId
        )
        toAwait.push(...phoneResult.toAwaitOps)
        wasUnknownNumberMap = phoneResult.wasUnknownNumberMap
        phoneUpsertResult = phoneResult.phoneUpsertResult
      }
    }

    await Promise.all(toAwait)

    // If the number was previously owned by the unknown-texts patient, remove
    // that mapping first so the number isn't shared, then migrate the text
    // history to the real patient.
    if (wasUnknownNumberMap && phoneUpsertResult) {
      await migrateUnknownTextsToPatient(
        phoneUpsertResult.phoneNumberId,
        patient.entityId
      )
    }

    void usePatientStore().fetchContacts(patient.entityId)
  } catch (err) {
    console.error(err)
    _notifyContactError(err)
    return
  }

  _notifyContactSuccess()
}
